import type { KeyboardEvent } from 'react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils/classname';

// Shake animation for duplicate detection
const shakeKeyframes = `
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

.animate-shake {
  animation: shake 300ms ease-in-out;
}
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleId = 'multi-value-input-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = shakeKeyframes;
    document.head.appendChild(style);
  }
}

export interface MultiValueInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  editable: boolean;
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
  /** When set, renders an explicit add button next to the input with this label. Enter-key still works. */
  addButtonLabel?: string;
}

export const MultiValueInput = ({
  values,
  onChange,
  editable,
  placeholder = 'Type and press Enter...',
  className,
  'aria-label': ariaLabel,
  addButtonLabel,
}: MultiValueInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [shakingIndex, setShakingIndex] = useState<number | null>(null);

  const tryAddValue = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;
    const duplicateIndex = values.findIndex(v => v === trimmedValue);
    if (duplicateIndex !== -1) {
      setShakingIndex(duplicateIndex);
      setTimeout(() => setShakingIndex(null), 300);
      return;
    }
    onChange([...values, trimmedValue]);
    setInputValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryAddValue();
    }
  };

  const handleRemove = (indexToRemove: number) => {
    onChange(values.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="space-y-2">
      {editable &&
        (() => {
          const inputElement = (
            <input
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              className={cn(
                'bg-chariot-background-primary border-chariot-stroke text-chariot-text-primary focus:border-brand block w-full rounded-xs border p-2 pl-3 text-sm outline-0 focus:ring-0 focus:outline-hidden',
                className
              )}
              aria-label={ariaLabel}
            />
          );

          return addButtonLabel ? (
            <div className="flex gap-2">
              <div className="flex-1">{inputElement}</div>
              <Button
                size="sm"
                onClick={tryAddValue}
                disabled={!inputValue.trim()}
              >
                {addButtonLabel}
              </Button>
            </div>
          ) : (
            inputElement
          );
        })()}

      {values.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {values.map((value, index) => (
            <span
              key={index}
              className={cn(
                'border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm',
                shakingIndex === index && 'animate-shake'
              )}
            >
              {value}
              {editable && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="text-chariot-text-secondary hover:text-chariot-text-primary ml-0.5 min-h-6 min-w-6"
                  aria-label={`Remove ${value}`}
                >
                  &times;
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
